import React, { useEffect, useRef, useState } from 'react'
import { Container, Row, Col, Card, Form, Button, Spinner, Modal } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';
import moment from 'moment';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSelector } from 'react-redux';

// ── API Config ────────────────────────────────────────────────────────────────
const DUMP_BASE = `${process.env.REACT_APP_BASE_URL}/datadump`;

const APIS = {
  callingDump:    `${DUMP_BASE}/getCallingDataDumpCSV`,
  callingTracker: `${DUMP_BASE}/getCallingDataCSVTracke`,
  dialDump:       `${DUMP_BASE}/getDialCallDataDumpCSV`,
  dialTracker:    `${DUMP_BASE}/getDialCallDataCSVTracke`,
};

const POLL_MS = 3000;
const MAX_POLL_ATTEMPTS = 200; // 10 minutes max

// ── Progress Bar Component ────────────────────────────────────────────────────
const ExportProgress = ({ active, processed, total, label }) => {
  if (!active) return null;

  const pct    = total > 0 ? Math.min(Math.round((processed / total) * 100), 100) : 0;
  const isDone = pct === 100;

  return (
    <div className="mt-4 p-3 rounded-3 border" style={{ background: "#f8faff", borderColor: "#d0dff7" }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center gap-2">
          {isDone
            ? <Icon.CheckCircleFill className="text-success" size={15} />
            : <Spinner animation="border" size="sm" style={{ color: "#1a4db3", width: 14, height: 14, borderWidth: 2 }} />}
          <span className="fw-semibold" style={{ fontSize: 13, color: "#1a4db3" }}>
            {isDone ? `${label} — Ready to download` : `Processing ${label}…`}
          </span>
        </div>
        <span className="fw-bold" style={{ fontSize: 13, color: "#1a4db3" }}>{pct}%</span>
      </div>

      {/* Progress track */}
      <div className="rounded-pill overflow-hidden" style={{ height: 8, background: "#d9e5f7" }}>
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: "999px",
            background: isDone
              ? "linear-gradient(90deg,#22c55e,#16a34a)"
              : "linear-gradient(90deg,#3b82f6,#1a4db3)",
            transition: "width 0.5s ease",
          }}
        />
      </div>

      <div className="d-flex justify-content-between mt-2">
        <span style={{ fontSize: 11, color: "#64748b" }}>
          {processed.toLocaleString()} records processed
        </span>
        <span style={{ fontSize: 11, color: "#64748b" }}>
          {total.toLocaleString()} total
        </span>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const InternalDevNew = () => {

  const selectedFinancialYear = useSelector((state) => state.activeData.financialYear);
  const [loadingButton, setLoadingButton] = useState("");   // "Call" | "Dial Call" | ""
  const [startDate, setStartDate]         = useState('');
  const [endDate, setEndDate]             = useState('');
  const [startDateErr, setStartDateErr]   = useState(false);
  const [endDateErr, setEndDateErr]       = useState(false);
  const [showModal, setShowModal]         = useState(false);
  const [showAllModal, setShowAllModal]   = useState(false);

  const [progress, setProgress] = useState({
    active: false, processed: 0, total: 0, label: "",
  });

  const pollRef = useRef(null);
  const pollAttemptsRef = useRef(0);
  const requestIdRef = useRef(null);
  const dumpUrlRef = useRef(null);
  const paramsRef = useRef(null);
  const filenameRef = useRef(null);
  const isDownloadingRef = useRef(false);
  const isCompleteRef = useRef(false);
  const lastValidProgressRef = useRef({ processed: 0, total: 0 });
  const consecutiveEmptyRef = useRef(0);

  // ── Cleanup on unmount ──
  useEffect(() => () => stopPolling(), []);

  const stopPolling = () => {
    if (pollRef.current) { 
      clearInterval(pollRef.current); 
      pollRef.current = null; 
    }
    pollAttemptsRef.current = 0;
  };

  const resetExport = () => {
    setLoadingButton("");
    setProgress({ active: false, processed: 0, total: 0, label: "" });
    stopPolling();
    requestIdRef.current = null;
    dumpUrlRef.current = null;
    paramsRef.current = null;
    filenameRef.current = null;
    isDownloadingRef.current = false;
    isCompleteRef.current = false;
    lastValidProgressRef.current = { processed: 0, total: 0 };
    consecutiveEmptyRef.current = 0;
  };

  // ── Validation ──
  const validate = () => {
    let ok = true;
    if (!startDate) { setStartDateErr(true); ok = false; }
    if (!endDate)   { setEndDateErr(true);   ok = false; }
    return ok;
  };

  // ── CSV download trigger ──
  const downloadCSV = async (dumpUrl, params, filename) => {
    try {
      
      const res = await fetch(`${dumpUrl}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Accept': 'text/csv, application/octet-stream, */*',
        }
      });
      
      if (!res.ok) {
        throw new Error(`Download failed with status: ${res.status}`);
      }

      const blob = await res.blob();
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 5000);
      
      return true;
    } catch (err) {
      console.error("CSV download error:", err);
      toast.error(`Download failed: ${err.message}`);
      return false;
    }
  };

    // ── Tracker polling ──
  const startTracking = (trackerUrl, requestId, onComplete, onNoData) => {
    stopPolling();

    let done = false;
    let lastKnownTotal = 0;
    let noDataAttempts = 0;
    const MAX_NO_DATA_ATTEMPTS = 3;

    const checkProgress = async () => {
      if (done) return;

      try {
        const res = await fetch(
          `${trackerUrl}?requestId=${encodeURIComponent(requestId)}`,
          { cache: 'no-store' }
        );

        if (res.status === 304) {
          noDataAttempts++;
          if (noDataAttempts >= MAX_NO_DATA_ATTEMPTS) {
            done = true;
            stopPolling();
            onNoData();
          }
          return;
        }

        const json = await res.json();
        const { processedCount = 0, totalCount = 0 } = json?.data || {};

        console.log(`Progress: ${processedCount}/${totalCount}`);

        if (totalCount > 0) {
          noDataAttempts = 0;
          lastKnownTotal = totalCount;

          setProgress(prev => ({
            ...prev,
            processed: processedCount,
            total: totalCount,
          }));

          if (processedCount >= totalCount) {
            done = true;
            stopPolling();
            onComplete();
          }
        } else {
          if (lastKnownTotal > 0) {
            done = true;
            stopPolling();
            onComplete();
          } else {
            noDataAttempts++;

            if (noDataAttempts >= MAX_NO_DATA_ATTEMPTS) {
              done = true;
              stopPolling();
              onNoData();
            }
          }
        }
      } catch (err) {
        console.error(err);
      }
    };

    // ✅ First call immediately
    checkProgress();

    // ✅ Then poll every 3 sec
    pollRef.current = setInterval(checkProgress, POLL_MS);
  };

  const handleComplete = async () => {
    if (isCompleteRef.current) {
      console.log("⚠️ Already complete, skipping...");
      return;
    }
    isCompleteRef.current = true;
    // STOP polling immediately
    stopPolling();
    if (isDownloadingRef.current) {
      console.log("⚠️ Download already in progress, skipping...");
      return;
    }
    
    isDownloadingRef.current = true;

    // Small delay to ensure file is ready on server
    setTimeout(async () => {
      try {
        // Download the CSV
        const downloadSuccess = await downloadCSV(
          dumpUrlRef.current, 
          new URLSearchParams(paramsRef.current), 
          filenameRef.current
        );

        if (downloadSuccess) {
          toast.success(`${progress.label || 'Export'} completed successfully!`);
        } else {
          toast.error("Failed to download the file. Please try again.");
        }
      } catch (err) {
        console.error("Download error:", err);
        toast.error("Failed to complete export. Please try again.");
      } finally {
        // Reset everything
        resetExport();
        setShowModal(false);
        setShowAllModal(false);
        isDownloadingRef.current = false;
        isCompleteRef.current = false;
      }
    }, 3000); // 3 second delay
  };

  const handleExport = async (type, isAll = false) => {
    const isCall   = type === "Call";
    const label    = isCall ? "Call Logs" : "Dialed Call Logs";
    const dumpUrl  = isCall ? APIS.callingDump    : APIS.dialDump;
    const trackUrl = isCall ? APIS.callingTracker : APIS.dialTracker;

    setLoadingButton(type);
    setProgress({ active: true, processed: 0, total: 0, label });

    try {
      const params = new URLSearchParams({
        financialYear: selectedFinancialYear || "2024-2025",
      });
      if (!isAll) {
        params.set("fromDate", startDate);
        params.set("toDate", endDate);
      }

      const initRes = await fetch(`${dumpUrl}?${params.toString()}`);

      if (!initRes.ok) {
        toast.error("Failed to initiate export.");
        resetExport();
        return;
      }

      const requestId = initRes.headers.get("x-request-id");
    
      if (!requestId) {
        toast.error("Server did not return a Request ID.");
        resetExport();
        return;
      }

      //Blob pehli call se hi stream karna shuru — dobara fetch nahi
      const blobPromise = initRes.blob();

      startTracking(trackUrl, requestId, async () => {
        const datePart = isAll
          ? "All Data"
          : `${moment(startDate).format("DD-MM-YYYY")} To ${moment(endDate).format("DD-MM-YYYY")}`;
        const filename = `${label} Report — ${datePart}.csv`;

        //Blob ready hoga jab tak tracker complete hota hai
        const blob = await blobPromise;
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement("a");
        a.href     = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success(`${label} exported successfully!`);
        resetExport();
        setShowModal(false);
        setShowAllModal(false);
      });

    } catch (err) {
      console.error("Export error:", err);
      toast.error("Export failed.");
      resetExport();
    }
  };

  // ── Shared export button pair ──
  const ExportButtons = ({ isAll }) => (
    <Row className="g-3">
      <Col xs={12} sm={6}>
        <Button
          className="btn-submit w-100"
          onClick={() => handleExport("Call", isAll)}
          disabled={!!loadingButton && loadingButton !== "Call"}
        >
          {loadingButton === "Call"
            ? <><Spinner animation="border" size="sm" className="me-2" />Processing…</>
            : <><Icon.TelephoneInbound className="me-2" />Call Logs</>}
        </Button>
      </Col>
      <Col xs={12} sm={6}>
        <Button
          className="btn-submit w-100"
          onClick={() => handleExport("Dial Call", isAll)}
          disabled={!!loadingButton && loadingButton !== "Dial Call"}
        >
          {loadingButton === "Dial Call"
            ? <><Spinner animation="border" size="sm" className="me-2" />Processing…</>
            : <><Icon.TelephoneOutbound className="me-2" />Dialed Call Logs</>}
        </Button>
      </Col>
    </Row>
  );

  const handleModalClose = (closeModal) => {
    if (loadingButton) {
      toast.warning("Export in progress. Please wait for completion.");
      return;
    }
    closeModal();
    resetExport();
  };

  
  return (
    <div className="InternalDev">
      <ToastContainer position="top-right" autoClose={4000} />

      <Container>
        <div className="d-flex justify-content-center align-items-center mb-4">
          <h3 className="mb-0">Export Call Logs (Internal Dev)</h3>
        </div>

        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card className="shadow-sm rounded-4 overflow-hidden">

              <Card.Header className="py-3 text-center" style={{ background: "#1a4db3", color: "#fff" }}>
                <Icon.FileEarmarkArrowDown size={20} className="me-2" />
                <span className="fw-semibold">Call & Dialed Call Log Reports</span>
              </Card.Header>

              <Card.Body className="py-4 px-4">

                <div className="d-flex align-items-center gap-2 mb-3">
                  <div style={{ width: 3, height: 18, borderRadius: 4, background: "#1a4db3" }} />
                  <span className="fw-semibold text-muted" style={{ fontSize: 13, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                    Date Range Export
                  </span>
                </div>

                <Row className="g-3 mb-3">
                  <Col xs={12} sm={6}>
                    <Form.Group>
                      <Form.Label className="small fw-semibold text-secondary mb-1">
                        <Icon.CalendarEvent size={12} className="me-1" />Start Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={startDate}
                        max={new Date().toISOString().split("T")[0]}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          setStartDateErr(!e.target.value);
                          setEndDate('');
                          setEndDateErr(false);
                        }}
                        className={startDateErr ? "is-invalid" : ""}
                      />
                      {startDateErr && <div className="invalid-feedback">Please select a start date</div>}
                    </Form.Group>
                  </Col>

                  <Col xs={12} sm={6}>
                    <Form.Group>
                      <Form.Label className="small fw-semibold text-secondary mb-1">
                        <Icon.CalendarCheck size={12} className="me-1" />End Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        disabled={!startDate}
                        min={startDate}
                        max={new Date().toISOString().split("T")[0]}
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          setEndDateErr(!e.target.value);
                        }}
                        className={endDateErr ? "is-invalid" : ""}
                      />
                      {endDateErr && <div className="invalid-feedback">Please select an end date</div>}
                    </Form.Group>
                  </Col>
                </Row>

                <Button
                  className="btn-submit w-100"
                  onClick={() => { if (validate()) setShowModal(true); }}
                >
                  <Icon.ArrowDownCircle className="me-2" />Export by Date Range
                </Button>
              </Card.Body>

              <Card.Footer className="py-3 px-4" style={{ background: "#f0f4ff", borderTop: "1px solid #d0dff7" }}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div style={{ width: 3, height: 18, borderRadius: 4, background: "#64748b" }} />
                  <span className="fw-semibold text-muted" style={{ fontSize: 13, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                    Full Data Export
                  </span>
                </div>
                <Button
                  variant="outline-primary"
                  className="w-100"
                  style={{ borderColor: "#1a4db3", color: "#1a4db3" }}
                  onClick={() => setShowAllModal(true)}
                >
                  <Icon.DatabaseDown className="me-2" />Export All Data
                </Button>
              </Card.Footer>

            </Card>
          </Col>
        </Row>
      </Container>

      {/* ── Date-range Modal ── */}
      <Modal
        show={showModal}
        onHide={() => handleModalClose(() => setShowModal(false))}
        centered
        backdrop="static"
        keyboard={false}
        size="md"
      >
        <Modal.Header closeButton={!loadingButton} style={{ borderBottom: "1px solid #e2e8f0" }}>
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <Icon.ArrowDownCircle size={18} style={{ color: "#1a4db3" }} />
              <h6 className="mb-0 fw-bold">Select Export Type</h6>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 py-4">
          {startDate && endDate && (
            <div
              className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-4"
              style={{ background: "#eef3ff", border: "1px solid #c7d7f9", fontSize: 13 }}
            >
              <Icon.CalendarRange size={13} style={{ color: "#1a4db3" }} />
              <span style={{ color: "#1a4db3", fontWeight: 600 }}>
                {moment(startDate).format("DD MMM YYYY")} → {moment(endDate).format("DD MMM YYYY")}
              </span>
            </div>
          )}

          <ExportButtons isAll={false} />
          <ExportProgress {...progress} />
        </Modal.Body>
      </Modal>

      {/* ── All Data Modal ── */}
      <Modal
        show={showAllModal}
        onHide={() => handleModalClose(() => setShowAllModal(false))}
        centered
        backdrop="static"
        keyboard={false}
        size="md"
      >
        <Modal.Header closeButton={!loadingButton} style={{ borderBottom: "1px solid #e2e8f0" }}>
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <Icon.DatabaseDown size={18} style={{ color: "#1a4db3" }} />
              <h6 className="mb-0 fw-bold">Export All Data</h6>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 py-4">
          <p className="text-muted small mb-4">
            This will export the complete dataset for <strong>{selectedFinancialYear || "2024-2025"}</strong>. Large exports may take a few minutes.
          </p>

          <ExportButtons isAll={true} />
          <ExportProgress {...progress} />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default InternalDevNew;